// Pricing migration 32 (ADO-3985), T14 and T18: what the screen remembers about multi-buy deals for
// the session, in memory only (never stored, never sent).
//
// - `lost`: deals the shop dropped below and has not reached again (R3), per cart (the basket
//   store's cart key, `sessionCartKey`: the depot, the account and the entry kind), so another
//   depot's cart starts clean. The partway copy then says how to get the free case back, or reach
//   the deal again (21w, 21s, 21w-b, 21s-b); the deal leaves its cart's set once its progress
//   gives free units again, or once that cart holds none of its cases (a send, a clear, every
//   line removed), when the plain nudge shows instead.
// - `dismissed`: the incomplete-deal flags the shop closed with ✕, with each deal's pooled count
//   at the time (R8, 00: "closing it hides it for that deal for the session; it returns if the
//   deal's progress changes"). The lines keep their nudges (14a).
// - `dismissedReviewFlags`: the order review's own flag closed with ✕, by the same rule; the side
//   cart's closed flag hides nothing at review (R8 14a).
// - `dismissedNudges`: the order review's in-line nudges closed with ✕, by the same rule (a held
//   derivation: 19 draws the ✕ but not what it does).

import { create } from 'zustand';
import { cartTargetKey } from '../api/cart-target';
import { useSessionCartTarget } from '../basket/sessionCart';

interface DealSessionState {
  /** By cart key, the offer codes lost in that cart. */
  readonly lost: ReadonlyMap<string, ReadonlySet<string>>;
  readonly dismissed: ReadonlyMap<string, number>;
  readonly dismissedNudges: ReadonlyMap<string, number>;
  /** The order review's flag, closed on its own: the side cart's ✕ hides nothing at review (R8
   *  14a: "order review (19) still lists incomplete deals"). */
  readonly dismissedReviewFlags: ReadonlyMap<string, number>;
  markLost: (cartKey: string, offerCode: string) => void;
  /** The deal is reached again, or has no cases left in the cart. */
  forgetLoss: (cartKey: string, offerCode: string) => void;
  /** Hide the flags of these deals, each at its pooled count now. */
  dismissFlags: (
    deals: ReadonlyArray<{ readonly offerCode: string; readonly pooled: number }>,
  ) => void;
  /** Hide the order review's flag for these deals, each at its pooled count now. */
  dismissReviewFlags: (
    deals: ReadonlyArray<{ readonly offerCode: string; readonly pooled: number }>,
  ) => void;
  /** Hide one deal's review nudge at its pooled count now. */
  dismissNudge: (offerCode: string, pooled: number) => void;
  /** Show a closed review nudge again ("Show me ↓" on the flag leads to it). */
  restoreNudge: (offerCode: string) => void;
  reset: () => void;
}

export const useDealSessionStore = create<DealSessionState>()((set, get) => ({
  lost: new Map(),
  dismissed: new Map(),
  dismissedNudges: new Map(),
  dismissedReviewFlags: new Map(),
  markLost(cartKey, offerCode) {
    const inCart = lostIn(get().lost, cartKey);
    if (inCart.has(offerCode)) return;
    set({ lost: new Map([...get().lost, [cartKey, new Set([...inCart, offerCode])]]) });
  },
  forgetLoss(cartKey, offerCode) {
    const inCart = lostIn(get().lost, cartKey);
    if (!inCart.has(offerCode)) return;
    const left = new Set(inCart);
    left.delete(offerCode);
    const lost = new Map(get().lost);
    if (left.size === 0) lost.delete(cartKey);
    else lost.set(cartKey, left);
    set({ lost });
  },
  dismissFlags(deals) {
    const dismissed = new Map(get().dismissed);
    for (const deal of deals) dismissed.set(deal.offerCode, deal.pooled);
    set({ dismissed });
  },
  dismissReviewFlags(deals) {
    const dismissedReviewFlags = new Map(get().dismissedReviewFlags);
    for (const deal of deals) dismissedReviewFlags.set(deal.offerCode, deal.pooled);
    set({ dismissedReviewFlags });
  },
  dismissNudge(offerCode, pooled) {
    set({ dismissedNudges: new Map([...get().dismissedNudges, [offerCode, pooled]]) });
  },
  restoreNudge(offerCode) {
    if (!get().dismissedNudges.has(offerCode)) return;
    const dismissedNudges = new Map(get().dismissedNudges);
    dismissedNudges.delete(offerCode);
    set({ dismissedNudges });
  },
  reset() {
    set({
      lost: new Map(),
      dismissed: new Map(),
      dismissedNudges: new Map(),
      dismissedReviewFlags: new Map(),
    });
  },
}));

const NONE_LOST: ReadonlySet<string> = new Set();

/** The deals lost in the cart `cartKey` (none with no cart). The same set until it changes, so it
 *  serves as a store selector. */
export function lostIn(
  lost: ReadonlyMap<string, ReadonlySet<string>>,
  cartKey: string | null,
): ReadonlySet<string> {
  return (cartKey === null ? undefined : lost.get(cartKey)) ?? NONE_LOST;
}

/** The deals lost in the session's cart, as a SUBSCRIBED value. */
export function useLostDeals(): ReadonlySet<string> {
  const cart = useSessionCartTarget();
  const cartKey = cart === null ? null : cartTargetKey(cart);
  return useDealSessionStore((s) => lostIn(s.lost, cartKey));
}

/** Whether the shop closed this deal's flag and its progress has not changed since (R8). */
export function flagDismissed(
  dismissed: ReadonlyMap<string, number>,
  offerCode: string,
  pooled: number,
): boolean {
  return dismissed.get(offerCode) === pooled;
}
